import { useCallback, useEffect, useState } from "react";
import * as Schema from "effect/Schema";
import { IManageStatus } from "@amoreg/contracts/imanage";
import { resolvePrimaryEnvironmentHttpUrl } from "../environments/primary/target";

export async function imanageRequest(
  organizationId: string,
  action: string,
  params: Record<string, string> = {},
  body?: unknown,
  signal?: AbortSignal,
): Promise<unknown> {
  const response = await fetch(
    resolvePrimaryEnvironmentHttpUrl(`/api/amoreg/imanage/${action}`, {
      organizationId,
      ...params,
    }),
    {
      credentials: "include",
      method: body === undefined ? "GET" : "POST",
      ...(body === undefined
        ? {}
        : { headers: { "content-type": "application/json" }, body: JSON.stringify(body) }),
      signal: signal
        ? AbortSignal.any([signal, AbortSignal.timeout(90_000)])
        : AbortSignal.timeout(90_000),
    },
  );
  const value: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const detail =
      typeof value === "object" &&
      value !== null &&
      "error" in value &&
      typeof value.error === "string"
        ? value.error
        : "The iManage service is unavailable.";
    throw new Error(detail);
  }
  return value;
}
export function useIManageStatus(organizationId: string | null) {
  const [state, setState] = useState<{ org: string; value: IManageStatus } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => setRevision((x) => x + 1), []);
  useEffect(() => {
    setError(null);
  }, [organizationId]);
  useEffect(() => {
    if (!organizationId) return;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let sequence = 0;
    let failures = 0;
    const load = async () => {
      const request = ++sequence;
      try {
        const value = Schema.decodeUnknownSync(IManageStatus)(
          await imanageRequest(organizationId, "status", {}, undefined, controller.signal),
        );
        if (controller.signal.aborted || request !== sequence) return;
        failures = 0;
        setState({ org: organizationId, value });
        setError(null);
        if (value.pending) timer = setTimeout(load, 2000);
      } catch (e) {
        if (!controller.signal.aborted && request === sequence) {
          setError(e instanceof Error ? e.message : "Could not read iManage status.");
          timer = setTimeout(load, Math.min(30_000, 2000 * 2 ** Math.min(failures++, 4)));
        }
      }
    };
    void load();
    const focus = () => {
      clearTimeout(timer);
      void load();
    };
    window.addEventListener("focus", focus);
    return () => {
      controller.abort();
      clearTimeout(timer);
      window.removeEventListener("focus", focus);
    };
  }, [organizationId, revision]);
  return { status: state?.org === organizationId ? state.value : null, error, refresh };
}
export function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i += 8192)
    binary += String.fromCharCode(...bytes.subarray(i, i + 8192));
  return btoa(binary);
}
export function base64ToBytes(value: string): Uint8Array {
  return Uint8Array.from(atob(value), (c) => c.charCodeAt(0));
}
